import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { installSessionGuard } from './services/sessionGuard';
import { installRetryFetch } from './utils/retryFetch';

// Retry goes on FIRST so it ends up the inner layer: the session guard then
// judges the answer that was finally received, rather than a 504 from a server
// that was restarting and came back a second later.
//
// Idempotent requests only — a GET asked twice is the same GET, a POST asked
// twice can place the same order twice.
installRetryFetch();

// Before any component can fetch: a 401 on a request that carried a token now
// ends that session instead of being swallowed by ~50 hand-rolled callers.
installSessionGuard();

// Sentry is loaded lazily (see below) to keep it out of the main bundle / off the
// critical path. Once loaded, this ref lets the error boundary forward to it.
let sentryRef: typeof import('@sentry/react') | null = null;

// Lightweight error boundary — no Sentry dependency at parse time, so the SDK
// stays out of main.js. Forwards caught errors to Sentry if/when it's loaded.
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    sentryRef?.captureException(error, { extra: { componentStack: info.componentStack } });
  }
  render() {
    if (this.state.hasError) {
      return <p style={{ padding: 24, textAlign: 'center' }}>Something went wrong. Please refresh the page.</p>;
    }
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);

// Load + init Sentry after the app is interactive (idle time), and only when a DSN
// is configured. This pulls the SDK into its own chunk so it never blocks first paint.
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;
if (sentryDsn) {
  const initSentry = () => {
    import('@sentry/react')
      .then(Sentry => {
        sentryRef = Sentry;
        Sentry.init({
          dsn: sentryDsn,
          environment: process.env.NODE_ENV,
          tracesSampleRate: 0, // errors only — no perf/session-replay sampling
        });
      })
      .catch(() => { /* monitoring is best-effort */ });
  };
  if ('requestIdleCallback' in window) {
    (window as any).requestIdleCallback(initSentry, { timeout: 4000 });
  } else {
    setTimeout(initSentry, 3000);
  }
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
